import React from "react";

// What kind of room a screen is in, roughly how many people pass through it,
// and how long they stay.
//
// One component, used by the browse card and the screen's own page, for the
// same reason `utils/venue.summarise()` is one function on the server: a screen
// must not describe its audience one way in the listing and another way on the
// page the listing links to.
//
// --- the rules this component exists to keep -------------------------------
//
// **Nothing is invented.** Every field is rendered only if the server sent it.
// A screen whose host has said nothing shows nothing at all -- not "typical",
// not a middle band, not "unknown audience". Once a figure is on the card it
// is indistinguishable from a declared one, and this one has a price attached.
// Same rule as `orientation`, which stays unknown rather than guessing
// landscape.
//
// **The visitor figure is always marked as the host's estimate.** Nothing
// measures footfall -- there is no counter and no camera -- and a higher band
// raises what the screen can charge, so there is a standing incentive to
// inflate it. The server sends `visitors_disclosure` alongside the number
// precisely so a client cannot render one without the other. Do not drop it to
// save a line: that turns a claim into a measurement.
//
// **Dwell says whose claim it is.** `dwell_source` is "host" when they set it
// and "venue" when it is the category default, and the two are worded
// differently. "Visits usually run 30 to 60 minutes" is a statement about
// salons; "the host says visits run 30 to 60 minutes" is a statement about
// this salon. Flattening them would put words in a host's mouth.

function ClockIcon() {
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <circle cx="8" cy="8" r="6.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M8 4.4V8l2.4 1.6" fill="none" stroke="currentColor"
                  strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function PeopleIcon() {
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <circle cx="6" cy="5.2" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M1.6 13.4c0-2.4 2-4 4.4-4s4.4 1.6 4.4 4" fill="none"
                  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M11.4 3.1a2.6 2.6 0 0 1 0 4.9M12.3 9.7c1.3.5 2.1 1.8 2.1 3.7"
                  fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    );
}

export default function VenueFacts({ device, className = "" }) {
    if (!device) return null;

    const venue = device.venue_label;
    const placement = device.placement_label;
    const visitors = device.visitors_label;
    const dwell = device.dwell_label;

    // Nothing declared at all. Render nothing rather than an empty shell --
    // "Audience: not stated" is a row of dead space on every legacy screen.
    if (!venue && !visitors && !dwell) return null;

    return (
        <div className={"vf " + className}>
            {venue && (
                <p className="vf__venue">
                    {venue}
                    {/* Where in the room, when the host has said. "Salon ·
                        Waiting area" tells an advertiser more than either
                        half alone. */}
                    {placement && <span className="vf__placement"> · {placement}</span>}
                </p>
            )}

            {(visitors || dwell) && (
                <ul className="vf__facts">
                    {visitors && (
                        <li className="vf__fact">
                            <span className="vf__icon"><PeopleIcon /></span>
                            <span>
                                {visitors}
                                {/* The qualifier travels with the number, in the
                                    same element, so no future refactor can move
                                    the figure without it. */}
                                {device.visitors_disclosure && (
                                    <span className="vf__qualifier"> · host’s estimate</span>
                                )}
                            </span>
                        </li>
                    )}
                    {dwell && (
                        <li className="vf__fact">
                            <span className="vf__icon"><ClockIcon /></span>
                            <span>
                                {device.dwell_source === "host"
                                    ? `Visits last ${dwell.toLowerCase()}`
                                    : `Visits here usually last ${dwell.toLowerCase()}`}
                            </span>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
